import {
  ACTIVITY_EVENT_TYPES,
  adherenceTrendQuery,
  adherenceWeightChangeQuery,
  exportActivityEvents,
  mealPlanAdherenceQuery,
  retentionCurveQuery,
  rollingActiveUsersDailyQuery,
  rollingActiveUsersMonthlyQuery,
  rollingActiveUsersWeeklyQuery,
  usersActiveCountQuery,
  usersEnrolledCountQuery,
} from '@/api/usage-statistics'
import { Button } from '@/components/Button'
import { ChartPanel } from '@/components/ChartPanel/ChartPanel'
import { LineChart, ScatterChart, TimeSeriesChart } from '@/components/Charts/Charts'
import { Checkbox } from '@/components/Checkbox'
import { Flex } from '@/components/Flex'
import { Popover } from '@/components/Popover'
import { Spinner } from '@/components/Spinner'
import { Grid } from '@/components/Grid'
import EngagementKPI from '@/features/stats/EngagementKPI'
import EngagementRangeSelector from '@/features/stats/EngagementRangeSelector'
import { DialogTrigger } from 'react-aria-components'
import { createFileRoute } from '@tanstack/react-router'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type RangeType = 'week' | 'month' | 'quarter' | 'custom'

// i18n key (under usageStats.eventTypes) for each backend event type
const EVENT_TYPE_LABEL_KEYS = {
  FOOD_LOG: 'foodLog',
  RECIPE_FAVORITE: 'recipeFavorite',
  RECIPE_LESS_OFTEN: 'recipeLessOften',
  RECIPE_NOT_RECOMMEND: 'recipeNotRecommend',
  INGREDIENT_FAVORITE: 'ingredientFavorite',
  MEAL_PLAN: 'mealPlan',
} as const satisfies Record<(typeof ACTIVITY_EVENT_TYPES)[number], string>

export const Route = createFileRoute('/usage-statistics/engagement')({
  loaderDeps: ({ search }) => search,
  validateSearch: (search: Record<string, unknown>) => {
    // Accept ISO strings or Date objects for the date fields
    const parseMaybeDate = (v: unknown, fallback: Date) => {
      if (v instanceof Date) return v
      if (typeof v === 'string') {
        const d = new Date(v)
        return isNaN(d.getTime()) ? fallback : d
      }
      return fallback
    }

    if (search.timeRange === 'custom') {
      return {
        timeRange: 'custom' as RangeType,
        startDate: parseMaybeDate(search.startDate, new Date()),
        endDate: parseMaybeDate(search.endDate, new Date()),
      }
    }

    return {
      timeRange: (search.timeRange || 'week') as RangeType,
    }
  },
  loader: async ({ context: { queryClient }, deps }) => {
    const { startDate, endDate } = convertRangeToDate(deps.timeRange, deps.startDate, deps.endDate)

    const [
      usersEnrolled,
      usersActive,
      rollingActiveUsersDaily,
      rollingActiveUsersWeekly,
      rollingActiveUsersMonthly,
      mealPlanAdherence,
      adherenceTrend,
      adherenceWeightChange,
      retentionCurve,
    ] = await Promise.all([
      queryClient.ensureQueryData(usersEnrolledCountQuery()),
      queryClient.ensureQueryData(usersActiveCountQuery()),
      queryClient.ensureQueryData(
        rollingActiveUsersDailyQuery({
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(
        rollingActiveUsersWeeklyQuery({
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(
        rollingActiveUsersMonthlyQuery({
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(mealPlanAdherenceQuery({})),
      queryClient.ensureQueryData(
        adherenceTrendQuery({
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(
        adherenceWeightChangeQuery({
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(
        retentionCurveQuery({
          startDate,
          endDate,
        }),
      ),
    ])

    return {
      startDate,
      endDate,
      usersEnrolled,
      usersActive,
      rollingActiveUsersDaily,
      rollingActiveUsersWeekly,
      rollingActiveUsersMonthly,
      mealPlanAdherence,
      adherenceTrend,
      adherenceWeightChange,
      retentionCurve,
    }
  },
  component: RouteComponent,
})

function formatIsoWeek(date: Date): string {
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const day = utcDate.getUTCDay() || 7
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day)
  const year = utcDate.getUTCFullYear()
  const yearStart = new Date(Date.UTC(year, 0, 1))
  const week = Math.ceil(((utcDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${year}-W${week.toString().padStart(2, '0')}`
}

function RouteComponent() {
  const {
    startDate,
    endDate,
    usersEnrolled,
    usersActive,
    rollingActiveUsersDaily,
    rollingActiveUsersWeekly,
    rollingActiveUsersMonthly,
    mealPlanAdherence,
    adherenceTrend,
    adherenceWeightChange,
    retentionCurve,
  } = Route.useLoaderData()
  const search = Route.useSearch()
  const { t } = useTranslation()
  const [exporting, setExporting] = useState(false)
  const [includeNutrition, setIncludeNutrition] = useState(false)
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(
    () => new Set(ACTIVITY_EVENT_TYPES),
  )

  const toggleEventType = (eventType: string, selected: boolean) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(eventType)
      } else {
        next.delete(eventType)
      }
      return next
    })
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const blob = await exportActivityEvents({
        startDate,
        endDate,
        includeNutrition,
        eventTypes: [...selectedEventTypes],
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `activity-events-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export activity events:', error)
    } finally {
      setExporting(false)
    }
  }
  const nf = useMemo(() => new Intl.NumberFormat(undefined), [])
  const nfPercent = useMemo(
    () => new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }),
    [],
  )

  const xTickFmt = useMemo(() => {
    const fmtDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: '2-digit' })
    const fmtMonth = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' })
    return (d: any) => {
      const dt = d instanceof Date ? d : new Date(d)

      if (search.timeRange === 'month') return fmtMonth.format(dt)
      // for day and week show day-month
      return fmtDay.format(dt)
    }
  }, [search.timeRange])

  // Food log per day chart transform
  const rollingActiveSeries = useMemo(() => {
    // const gb = getGroupBy(search.startDate, search.endDate)
    const byGroup =
      search.timeRange === 'week'
        ? (rollingActiveUsersWeekly ?? [])
        : search.timeRange === 'month'
          ? (rollingActiveUsersMonthly ?? [])
          : (rollingActiveUsersDaily ?? [])

    return byGroup
      .map((point) => ({ date: new Date(point.date), value: point.count }))
      .sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [
    rollingActiveUsersDaily,
    rollingActiveUsersWeekly,
    rollingActiveUsersMonthly,
    startDate,
    endDate,
  ])

  const adherenceTrendSeries = useMemo(
    () =>
      (adherenceTrend ?? [])
        .map((point) => ({ date: new Date(point.date), value: point.value }))
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    [adherenceTrend],
  )

  const adherenceWeightScatter = useMemo(
    () =>
      (adherenceWeightChange ?? []).map((point) => ({
        x: point.adherence,
        y: point.weightChange,
      })),
    [adherenceWeightChange],
  )

  return (
    <Flex direction="column" align="stretch" style={{ marginTop: 16 }}>
      <Flex direction="row" justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <EngagementRangeSelector
          rangeType={search.timeRange}
          startDate={startDate}
          endDate={endDate}
        />
        <Flex direction="row" align="center" gap={16}>
          <DialogTrigger>
            <Button>
              {selectedEventTypes.size === ACTIVITY_EVENT_TYPES.length
                ? t('usageStats.eventTypeFilter')
                : `${t('usageStats.eventTypeFilter')} (${selectedEventTypes.size})`}
              {' ▾'}
            </Button>
            <Popover>
              <Flex direction="column" align="start" gap={8} style={{ padding: 12 }}>
                {ACTIVITY_EVENT_TYPES.map((eventType) => (
                  <Checkbox
                    key={eventType}
                    isSelected={selectedEventTypes.has(eventType)}
                    onChange={(selected) => toggleEventType(eventType, selected)}
                  >
                    {t(`usageStats.eventTypes.${EVENT_TYPE_LABEL_KEYS[eventType]}`)}
                  </Checkbox>
                ))}
              </Flex>
            </Popover>
          </DialogTrigger>
          <Checkbox isSelected={includeNutrition} onChange={setIncludeNutrition}>
            {t('usageStats.includeNutrition')}
          </Checkbox>
          <Button
            onPress={handleExport}
            isDisabled={exporting || selectedEventTypes.size === 0}
          >
            {exporting ? <Spinner /> : t('usageStats.exportActivityEvents')}
          </Button>
        </Flex>
      </Flex>
      <EngagementKPI
        activeUsers={usersActive.length}
        enrolledUsers={usersEnrolled.length}
        adherencePercentage={mealPlanAdherence?.adherencePercentage}
      />

      {/* Charts only for Engagement */}
      <Grid columns="repeat(auto-fit, minmax(500px, 1fr))">
        <ChartPanel title={t('usageStats.adherenceTrend')}>
          <TimeSeriesChart
            data={adherenceTrendSeries}
            xLabel={t('usageStats.week')}
            yLabel={t('usageStats.adherencePercent')}
            title={undefined}
            xTickFormat={(d) => formatIsoWeek(d instanceof Date ? d : new Date(d))}
            yTickFormat={(v) => nfPercent.format((v ?? 0) / 100)}
          />
        </ChartPanel>

        <ChartPanel
          title={t('usageStats.rollingActiveUsers')}
          subtitle={t('usageStats.rollingActiveUsersLegend')}>
          <TimeSeriesChart
            data={rollingActiveSeries}
            xLabel={t('usageStats.date')}
            yLabel={t('usageStats.users')}
            title={undefined}
            xTickFormat={xTickFmt}
            yTickFormat={(v) => nf.format(v)}
          />
        </ChartPanel>

        <ChartPanel title={t('usageStats.retentionCurve')}>
          <LineChart
            data={retentionCurve}
            xLabel={t('usageStats.daysSinceEnrollment')}
            yLabel={t('usageStats.retentionPercent')}
            title={undefined}
            width={720}
            curve="step"
          />
        </ChartPanel>

        <ChartPanel title={t('usageStats.adherenceVsWeight')}>
          <ScatterChart
            data={adherenceWeightScatter}
            xLabel={t('usageStats.adherencePercent')}
            yLabel={t('usageStats.weightChangeKg')}
            title={undefined}
          />
        </ChartPanel>

        {/* <div className="stack-y">
          <div className="panel-xs">
            <h3 style={{ margin: 0 }}>{t('usageStats.foodLogPerWeekday')}</h3>
            <BarChart
              title={undefined}
              data={
                (foodLogCountPerWeekDay ?? []).map((d) => ({
                  weekDay: d.weekDay,
                  count: d.count,
                })) as any[]
              }
              x="weekDay"
              y="count"
              xLabel={t('usageStats.weekday')}
              yLabel={t('usageStats.count')}
              xDomain={weekDays}
              xTickFormat={(wd) => weekDayLabel(wd)}
            />
          </div>
          <div className="panel-xs">
            <h3 style={{ margin: 0 }}>{t('usageStats.foodLogPerHour')}</h3>
            <BarChart
              title={undefined}
              data={
                (foodLogCountPerHour ?? []).map((d) => ({ hour: d.hour, count: d.count })) as any[]
              }
              x="hour"
              y="count"
              xLabel={t('usageStats.hour')}
              yLabel={t('usageStats.count')}
              xDomain={Array.from({ length: 24 }, (_, i) => i)}
              xTickFormat={(h) => String(h).padStart(2, '0')}
            />
          </div>
        </div> */}
      </Grid>
    </Flex>
  )
}
function convertRangeToDate(
  value: RangeType,
  startDate: Date | undefined,
  endDate: Date | undefined,
) {
  if (value === 'custom' && startDate && endDate) {
    return {
      startDate,
      endDate,
    }
  }

  const end = new Date()
  const start = new Date()

  if (value === 'week') {
    start.setDate(end.getDate() - 6)
  } else if (value === 'month') {
    start.setDate(end.getDate() - 29)
  } else if (value === 'quarter') {
    start.setDate(end.getDate() - 89)
  }

  return {
    startDate: start,
    endDate: end,
  }
}
